import { useIsLaptop } from '@synoptic/ui-kit/responsive/hooks.ts';
import { memo } from 'react';
import { useMatch } from 'react-router';
import { StreamCardFragmentFragment } from '@/__generated__/graphql.ts';
import { CopyButton } from '@/components/copy-button/copy-button.tsx';
import { LayoutCard } from '@/components/layout/card.tsx';
import { FeaturedBadge } from '@/components/stream-list/stream-card/featured-badge.tsx';
import { StreamCardMobile } from '@/components/stream-list/stream-card/stream-card-mobile.tsx';
import { StreamStateIndicator } from '@/components/stream-list/stream-state-indicator.tsx';
import { StreamAccessTill } from '@/components/streams/stream-access-till/stream-access-till.tsx';
import { StreamAuthor } from '@/components/streams/stream-author/stream-author.tsx';
import { getStreamCategoryTitle } from '@/components/streams/stream-categories.ts';
import { StreamFollowers } from '@/components/streams/stream-followers/stream-followers.tsx';
import { StreamPriceTag } from '@/components/streams/stream-tag/stream-price-tag.tsx';
import { StreamStateTag } from '@/components/streams/stream-tag/stream-state-tag.tsx';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars.ts';
import { getMonthlyPrice } from '@/utils/paid-data.ts';
import {
  cardDescription,
  cardTitle,
  cardTitleContainer,
  categoryTag,
  categoryTagContainer,
  copyIdButton,
  streamCard,
  streamCardLeft,
  streamCardLink,
  streamCardRight,
  streamCardUsernameContainer,
  streamFollowersContainer,
  streamStateTagContainer,
} from './stream-card.css.ts';

type StreamCardProps = {
  stream: StreamCardFragmentFragment;
};
export const StreamCard = memo<StreamCardProps>(function StreamCardComponent({
  stream,
}) {
  const isLaptop = useIsLaptop();
  const isMyStreams = useMatch(paths.streamsCreated);

  return isLaptop ? (
    <LayoutCard className={streamCard}>
      <Link
        to={paths.makeStream(stream.id)}
        prefetch="intent"
        className={streamCardLink}
      >
        <div className={streamCardLeft}>
          <div className={cardTitleContainer}>
            <StreamStateIndicator streamState={stream.streamState} />
            <h3 className={cardTitle}>{stream.name}</h3>

            {stream.isFeatured && <FeaturedBadge />}

            {stream.subscription?.active && stream.subscription.cancelAt && (
              <StreamAccessTill cancelAt={stream.subscription.cancelAt} />
            )}
          </div>
          <p className={cardDescription}>{stream.description}</p>
        </div>

        <div className={streamCardRight}>
          <CopyButton
            className={copyIdButton}
            text={stream.id}
            title="copy id"
          />
          <StreamPriceTag
            price={
              stream.paidData?.length
                ? convertCentsToDollars(getMonthlyPrice(stream.paidData) ?? 0)
                : undefined
            }
            packageOnly={stream.packageOnly}
            data-testid={'streamCardPrice'}
          />
          <div
            className={categoryTagContainer}
            data-testid={'streamCardCategoryContainer'}
          >
            <div className={categoryTag}>
              <span>{getStreamCategoryTitle(stream.streamCategory)}</span>
            </div>
          </div>
          <StreamFollowers
            followersCount={stream.followersCount}
            className={streamFollowersContainer}
          />
          {isMyStreams ? (
            <div className={streamStateTagContainer}>
              <StreamStateTag
                state={stream.streamState}
                type={stream.streamType}
              />
            </div>
          ) : (
            <StreamAuthor
              author={stream.author}
              usernameContainerClassName={streamCardUsernameContainer}
            />
          )}
        </div>
      </Link>
    </LayoutCard>
  ) : (
    <StreamCardMobile stream={stream} />
  );
});
